import { AnimatePresence, motion, type Variants } from "motion/react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import type { EmbedConfig } from "@/lib/schemas/checkout/embed-config";
import type { Link } from "@/lib/schemas/paycode/link";
import { useCheckoutStore } from "@/lib/store/checkout";
import { cn } from "@/lib/utils";
import { BusinessLogo } from "../business-logo";
import { HeroIcon } from "../hero-icon";
import { Skeleton } from "../ui/skeleton";
import { EditProductsButton } from "./edit-products";
import { ImageCarousel } from "./image-carousel";
import { OrderSummary } from "./order-summary";
import { OrderTableSkeleton } from "./order-summary/table";

type DataPaneProps = {
  embedConfig?: EmbedConfig | null;
  embedded?: boolean;
  link?: Link | null;
};

const blurContentVariants: Variants = {
  initial: { opacity: 0, filter: "blur(4px)" },
  animate: { opacity: 1, filter: "blur(0px)" },
  exit: { opacity: 0, filter: "blur(4px)" },
};

export const DataPane = ({ embedConfig, embedded, link }: DataPaneProps) => {
  const t = useTranslations("products");
  const [expandedOrder, setExpandedOrder] = useState(false);
  const total = useCheckoutStore((s) => s.total);
  const products = useCheckoutStore((s) => s.products);

  const isDesktop = useMediaQuery("(min-width: 64rem)");

  const displayBusiness = embedded ? !!embedConfig?.renderBusiness : true;
  const displayOrderSummary = embedded
    ? !!embedConfig?.renderOrderSummary
    : true;
  const displayConcept = embedded ? !!embedConfig?.renderConcept : true;
  const displayCarousel = embedded ? !!embedConfig?.renderImageCarousel : true;

  return (
    <div className="flex flex-col items-center overflow-x-hidden bg-surface px-4 pt-8 text-foreground md:px-0 lg:basis-1/2 lg:items-end lg:bg-surface-alt lg:pt-16 lg:text-foreground-alt">
      <div className="flex h-full w-full max-w-md flex-col lg:pr-8 2xl:max-w-xl">
        <AnimatePresence initial={false} mode="popLayout">
          {isDesktop && expandedOrder ? (
            <motion.div key="expanded-order" className="flex flex-col gap-4">
              <motion.div
                key="order-summary-title"
                className="-ml-2 flex flex-row items-center space-x-2"
                variants={blurContentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <button
                  className="my-1 flex h-8 w-8 items-center justify-center rounded-lg hover:bg-white/5"
                  type="button"
                  onClick={() => setExpandedOrder(false)}
                >
                  <HeroIcon
                    name="arrow-long-left"
                    className="h-4 w-4"
                    iconStyle="micro"
                  />
                </button>
                <p className="line-clamp-1 font-medium text-xl">
                  {t("orderSummary")}
                </p>
              </motion.div>
              <motion.div layoutId="order-summary" layout="position">
                <OrderSummary
                  products={products}
                  total={total}
                  expanded={expandedOrder}
                  onExpandedChange={setExpandedOrder}
                  limit={3}
                />
                <EditProductsButton className="mx-auto mt-2 block hover:underline" />
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="collapsed-order"
              className="flex h-full w-full flex-col gap-4"
            >
              {displayBusiness && (
                <motion.div
                  key="business-info"
                  className="flex flex-row items-center space-x-2"
                  variants={blurContentVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <BusinessLogo
                    className="h-10 w-10"
                    src={link?.linkConfig.logo}
                    seed={link?.business}
                  />
                  <p className="line-clamp-1 font-business font-medium text-xl">
                    {link?.business}
                  </p>
                </motion.div>
              )}
              {displayConcept && (
                <motion.p
                  className="line-clamp-3 text-muted lg:text-muted-alt"
                  variants={blurContentVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  {link?.concept}
                </motion.p>
              )}
              {displayOrderSummary && (
                <motion.div
                  className="max-lg:order-1"
                  layoutId="order-summary"
                  layout="position"
                >
                  <OrderSummary
                    products={products}
                    total={total}
                    expanded={expandedOrder}
                    onExpandedChange={setExpandedOrder}
                    limit={3}
                  />
                  <EditProductsButton className="mx-auto mt-2 block hover:underline" />
                </motion.div>
              )}
              {displayCarousel && (
                <motion.div
                  variants={blurContentVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <ImageCarousel />
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export const DataPaneSkeleton = ({
  embedConfig,
  embedded,
  link,
}: DataPaneProps) => {
  const displayBusiness = embedded ? !!embedConfig?.renderBusiness : true;
  const displayOrderSummary = embedded
    ? !!embedConfig?.renderOrderSummary
    : true;
  const displayConcept = embedded ? !!embedConfig?.renderConcept : true;
  const displayCarousel = embedded ? !!embedConfig?.renderImageCarousel : true;

  // ponytail: char-count heuristic for line-clamp-3; real line count needs layout
  const conceptLines = link
    ? Math.min(3, Math.ceil(link.concept.length / 45))
    : 2;
  const carouselImages = link ? link.linkConfig.productImages.length : 0;

  return (
    <div className="flex flex-col items-center overflow-x-hidden bg-surface-static px-4 pt-8 text-foreground md:px-0 lg:basis-1/2 lg:items-end lg:bg-surface-alt-static lg:pt-16 lg:text-foreground-alt">
      <div className="flex h-full w-full max-w-md flex-col gap-4 lg:pr-8 2xl:max-w-xl">
        {displayBusiness && (
          <div className="flex flex-row items-center space-x-2">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Skeleton className="h-7 w-48 rounded-lg" />
          </div>
        )}
        {displayConcept && conceptLines > 0 && (
          <div className="flex flex-col space-y-1">
            {Array.from({ length: conceptLines }, (_, i) => (
              <Skeleton
                // biome-ignore lint/suspicious/noArrayIndexKey: identical static skeleton items
                key={`sk-concept-${i}`}
                className={cn("h-6", i + 1 === conceptLines ? "w-72" : "w-sm")}
              />
            ))}
          </div>
        )}
        {displayOrderSummary && (
          <div className="max-lg:order-1">
            <OrderTableSkeleton products={link?.products.length} limit={3} />
          </div>
        )}
        {displayCarousel && (!link || carouselImages > 0) && (
          <div className="-mx-4 md:-mx-6 lg:-ml-6 lg:-mr-8">
            <div className="flex gap-4 overflow-hidden pb-4">
              <div aria-hidden className="w-0 shrink-0 md:w-2 lg:w-4" />
              {Array.from(
                { length: link ? Math.min(carouselImages, 3) : 2 },
                (_, i) => (
                  <Skeleton
                    // biome-ignore lint/suspicious/noArrayIndexKey: identical static skeleton items
                    key={`sk-carousel-${i}`}
                    className={cn(
                      "h-56 shrink-0 rounded-lg",
                      i % 2 === 0 ? "w-80" : "w-40",
                    )}
                  />
                ),
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
